import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AmountFilterOperation } from 'constants/common';
import { AmountFilter } from 'components/custom-amount-filter/CustomAmountFilter';
import MoreFilters, { AppliedFilter, FilterDropdownType, FilterOption } from 'components/more-filters/MoreFilters';
import DropdownWithSearch from 'components/dropdown-with-search/DropdownWithSearch';
import {
  DISPUTE_STATUS_OPTIONS,
  ESTIMATE_STATUS_OPTIONS,
  INVOICE_MORE_FILTER_OPTIONS,
  INVOICE_STATUS_OPTIONS,
  InvoiceFilterIds,
  JOB_STATUS_OPTIONS,
  REFUND_STATUS_OPTIONS,
  REMINDER_STATUS_OPTIONS,
  TIP_COLLECTED_OPTIONS
} from '../constants';
import { getAmountQueryParam, getTextForFilter } from 'containers/dispatch/utils';
import CustomDateRangePicker from 'components/react-date-range-picker/ReactDateRangePicker';
import { useLazyGetAllCustomersQuery, useLazyGetMerchantsByTeamIdInvoiceQuery } from '../api';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import DateUtils from 'utils/dateUtils';
import { FilterOptionItem } from '../invoices.model';
import { getPaymentMethodFilters } from '../utils';
import { logAnalyticEvent } from 'utils/analytics';
import { CleverTapEventsInvoices } from '../events';

interface InitialFilters {
  customerId?: string[];
  createdBy?: string[];
}

interface InvoicesFilterProps {
  onFiltersChange: (filters: Record<string, unknown>) => void;
  initialFilters?: InitialFilters;
}

const InvoicesFilter: FC<InvoicesFilterProps> = memo(({ onFiltersChange, initialFilters }) => {
  const currentTeam = useAppSelector(selectCurrentTeam);

  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [selectedCustomersDraft, setSelectedCustomersDraft] = useState<string[]>([]);
  const [customerSearchString, setCustomerSearchString] = useState('');

  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedStatusesDraft, setSelectedStatusesDraft] = useState<string[]>([]);
  const [statusSearchString, setStatusSearchString] = useState('');

  const [createdAtFrom, setCreatedAtFrom] = useState<string | null>(null);
  const [createdAtTo, setCreatedAtTo] = useState<string | null>(null);

  const [dueDateFrom, setDueDateFrom] = useState<string | null>(null);
  const [dueDateTo, setDueDateTo] = useState<string | null>(null);

  const [createdBy, setCreatedBy] = useState<string[]>([]);
  const [createdByDraft, setCreatedByDraft] = useState<string[]>([]);
  const [createdBySearchString, setCreatedBySearchString] = useState('');

  const [amountFilter, setAmountFilter] = useState<AmountFilter>({
    filterOperation: AmountFilterOperation.ALL,
    minAmount: null,
    maxAmount: null
  });

  const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>([]);

  const [getCustomersQuery, getCustomersQueryState] = useLazyGetAllCustomersQuery();
  const [getMerchantsQuery, getMerchantsQueryState] = useLazyGetMerchantsByTeamIdInvoiceQuery();

  const { data: getCustomersResponse } = getCustomersQueryState;
  const { data: getMerchantsResponse } = getMerchantsQueryState;

  const customerOptions = useMemo(() => getCustomersResponse?.customers || [], [getCustomersResponse]);
  const merchantOptions = useMemo(() => getMerchantsResponse || [], [getMerchantsResponse]);

  const previousTeamIdRef = useRef<number | undefined>(currentTeam?.id);
  const isInitialMountRef = useRef<boolean>(true);
  const routeStateFiltersRef = useRef<{
    customerId?: string[];
    createdBy?: string[];
    synced?: boolean;
  } | null>(null);

  useEffect(() => {
    if (currentTeam?.id) {
      getCustomersQuery({ teamId: currentTeam.id });
      getMerchantsQuery({ teamId: currentTeam.id });
    }
  }, [currentTeam?.id, getCustomersQuery, getMerchantsQuery]);

  useEffect(() => {
    if (currentTeam?.id && previousTeamIdRef.current !== currentTeam?.id && previousTeamIdRef.current !== undefined) {
      setSelectedCustomers([]);
      setSelectedCustomersDraft([]);
      setCustomerSearchString('');
      setSelectedStatuses([]);
      setSelectedStatusesDraft([]);
      setStatusSearchString('');
      setCreatedAtFrom(null);
      setCreatedAtTo(null);
      setDueDateFrom(null);
      setDueDateTo(null);
      setCreatedBy([]);
      setCreatedByDraft([]);
      setCreatedBySearchString('');
      setAmountFilter({
        filterOperation: AmountFilterOperation.ALL,
        minAmount: null,
        maxAmount: null
      });
      setAppliedFilters([]);
      routeStateFiltersRef.current = null;
    }
    previousTeamIdRef.current = currentTeam?.id;
  }, [currentTeam?.id]);

  useEffect(() => {
    // Capture route state values when they first appear (before user changes)
    if (!routeStateFiltersRef.current && initialFilters) {
      if (initialFilters?.customerId?.length || initialFilters?.createdBy?.length) {
        routeStateFiltersRef.current = {
          customerId: initialFilters?.customerId,
          createdBy: initialFilters?.createdBy,
          synced: false
        };
      }
    }

    // Sync from captured route state values when options are loaded
    if (!routeStateFiltersRef.current || routeStateFiltersRef.current.synced) return;

    let synced = false;

    // Sync customerId if options are loaded
    if (routeStateFiltersRef.current?.customerId?.length && customerOptions.length > 0) {
      const validCustomerIds = routeStateFiltersRef.current.customerId.filter(
        customerId => customerOptions.some(customer => customer.ID === customerId)
        // eslint-disable-next-line function-paren-newline
      );

      if (validCustomerIds.length > 0) {
        setSelectedCustomers(validCustomerIds);
        setSelectedCustomersDraft(validCustomerIds);
        synced = true;
      }
    }

    // Sync createdBy if options are loaded
    if (routeStateFiltersRef.current?.createdBy?.length && merchantOptions.length > 0) {
      const validMerchantIds = routeStateFiltersRef.current.createdBy.filter(
        merchantId => merchantOptions.some(merchant => (merchant as { merchantId?: string }).merchantId === merchantId)
        // eslint-disable-next-line function-paren-newline
      );

      if (validMerchantIds.length > 0) {
        setCreatedBy(validMerchantIds);
        setCreatedByDraft(validMerchantIds);
        synced = true;
      }
    }

    // Mark as synced after syncing
    if (synced) {
      routeStateFiltersRef.current.synced = true;
    }
  }, [initialFilters, customerOptions, merchantOptions]);

  useEffect(() => {
    const refundStatusFilter = appliedFilters.find(f => f.id === InvoiceFilterIds.REFUND_STATUS);
    const disputeStatusFilter = appliedFilters.find(f => f.id === InvoiceFilterIds.DISPUTE_STATUS);
    const reminderStatusFilter = appliedFilters.find(f => f.id === InvoiceFilterIds.REMINDER_STATUS);
    const jobStatusFilter = appliedFilters.find(f => f.id === InvoiceFilterIds.JOB_STATUS);
    const estimateStatusFilter = appliedFilters.find(f => f.id === InvoiceFilterIds.ESTIMATE_STATUS);
    const tipCollectedFilter = appliedFilters.find(f => f.id === InvoiceFilterIds.TIP_COLLECTED);
    const paymentMethodsFilter = appliedFilters.find(f => f.id === InvoiceFilterIds.PAYMENT_METHODS);

    const refundStatusValue = refundStatusFilter?.value as string[] | undefined;
    const disputeStatusValue = disputeStatusFilter?.value as string[] | undefined;
    const reminderStatusValue = reminderStatusFilter?.value as string[] | undefined;
    const jobStatusValue = jobStatusFilter?.value as string[] | undefined;
    const estimateStatusValue = estimateStatusFilter?.value as string[] | undefined;
    const tipCollectedValue = tipCollectedFilter?.value as string[] | undefined;
    const paymentMethodsValue = paymentMethodsFilter?.value as string[] | undefined;

    const refundStatus = refundStatusValue?.[0]
      ? REFUND_STATUS_OPTIONS.find(opt => opt.id === refundStatusValue[0])?.id
      : undefined;

    const disputeStatus = disputeStatusValue?.[0]
      ? DISPUTE_STATUS_OPTIONS.find(opt => opt.id === disputeStatusValue[0])?.id
      : undefined;

    const reminderStatus = reminderStatusValue?.[0]
      ? REMINDER_STATUS_OPTIONS.find(opt => opt.id === reminderStatusValue[0])?.id
      : undefined;

    const jobStatus = jobStatusValue?.[0]
      ? JOB_STATUS_OPTIONS.find(opt => opt.id === jobStatusValue[0])?.id
      : undefined;

    const estimateStatus = estimateStatusValue?.[0]
      ? ESTIMATE_STATUS_OPTIONS.find(opt => opt.id === estimateStatusValue[0])?.id
      : undefined;

    const tipStatus =
      tipCollectedValue?.[0] && TIP_COLLECTED_OPTIONS.find(opt => opt.id === tipCollectedValue[0])?.id === 'tip'
        ? 'only_with_tip'
        : undefined;

    const filters = {
      customerId: selectedCustomers.length > 0 ? selectedCustomers : undefined,
      createdAtFrom: DateUtils.ensureUTCStartDateFormat(createdAtFrom),
      createdAtTo: DateUtils.ensureUTCEndDateFormat(createdAtTo),
      createdBy: createdBy.length > 0 ? createdBy : undefined,
      dueDateFrom: DateUtils.ensureUTCStartDateFormat(dueDateFrom),
      dueDateTo: DateUtils.ensureUTCEndDateFormat(dueDateTo),
      totalAmount: getAmountQueryParam(amountFilter) || undefined,
      invoiceStatus: selectedStatuses.length > 0 ? selectedStatuses : undefined,
      refundStatus,
      disputeStatus,
      reminderStatus,
      jobStatus,
      estimateStatus,
      paymentMethods: paymentMethodsValue && paymentMethodsValue.length > 0 ? paymentMethodsValue : undefined,
      tipStatus
    };

    onFiltersChange(filters);

    // Track filter usage (skip initial mount)
    if (!isInitialMountRef.current) {
      const hasActiveFilters =
        filters.customerId ||
        filters.createdAtFrom ||
        filters.createdAtTo ||
        filters.createdBy ||
        filters.dueDateFrom ||
        filters.dueDateTo ||
        filters.totalAmount ||
        filters.invoiceStatus ||
        filters.refundStatus ||
        filters.disputeStatus ||
        filters.reminderStatus ||
        filters.jobStatus ||
        filters.estimateStatus ||
        filters.paymentMethods ||
        filters.tipStatus;

      if (hasActiveFilters) {
        const selectedInvoiceStatusLabels = filters.invoiceStatus
          ? getTextForFilter(INVOICE_STATUS_OPTIONS, filters.invoiceStatus, 'id', 'label')
          : undefined;

        const refundStatusLabel = filters.refundStatus
          ? getTextForFilter(REFUND_STATUS_OPTIONS, [filters.refundStatus], 'id', 'label')
          : undefined;

        const disputeStatusLabel = filters.disputeStatus
          ? getTextForFilter(DISPUTE_STATUS_OPTIONS, [filters.disputeStatus], 'id', 'label')
          : undefined;

        const reminderStatusLabel = filters.reminderStatus
          ? getTextForFilter(REMINDER_STATUS_OPTIONS, [filters.reminderStatus], 'id', 'label')
          : undefined;

        const jobStatusLabel = filters.jobStatus
          ? getTextForFilter(JOB_STATUS_OPTIONS, [filters.jobStatus], 'id', 'label')
          : undefined;

        const estimateStatusLabel = filters.estimateStatus
          ? getTextForFilter(ESTIMATE_STATUS_OPTIONS, [filters.estimateStatus], 'id', 'label')
          : undefined;

        logAnalyticEvent(CleverTapEventsInvoices.webInvoicesListFiltersApplied, {
          teamId: currentTeam?.id?.toString(),
          hasCustomerFilter: !!filters.customerId,
          hasStatusFilter: !!filters.invoiceStatus,
          selectedInvoiceStatuses: selectedInvoiceStatusLabels,
          hasCreatedDateFilter: !!(filters.createdAtFrom || filters.createdAtTo),
          hasDueDateFilter: !!(filters.dueDateFrom || filters.dueDateTo),
          hasCreatedByFilter: !!filters.createdBy,
          hasAmountFilter: !!filters.totalAmount,
          hasRefundStatusFilter: !!filters.refundStatus,
          selectedRefundStatus: refundStatusLabel,
          hasDisputeStatusFilter: !!filters.disputeStatus,
          selectedDisputeStatus: disputeStatusLabel,
          hasReminderStatusFilter: !!filters.reminderStatus,
          selectedReminderStatus: reminderStatusLabel,
          hasJobStatusFilter: !!filters.jobStatus,
          selectedJobStatus: jobStatusLabel,
          hasEstimateStatusFilter: !!filters.estimateStatus,
          selectedEstimateStatus: estimateStatusLabel,
          hasPaymentMethodsFilter: !!filters.paymentMethods,
          hasTipStatusFilter: !!filters.tipStatus
        });
      }
    } else {
      isInitialMountRef.current = false;
    }
  }, [
    selectedCustomers,
    selectedStatuses,
    createdAtFrom,
    createdAtTo,
    createdBy,
    dueDateFrom,
    dueDateTo,
    amountFilter,
    appliedFilters,
    onFiltersChange,
    currentTeam?.id,
    currentTeam?.storeName
  ]);

  const getInvoiceFilterData = useCallback(
    (filterId: string): FilterOptionItem[] => {
      switch (filterId) {
        case InvoiceFilterIds.CREATED_BY:
          return merchantOptions as unknown as FilterOptionItem[];
        case InvoiceFilterIds.REFUND_STATUS:
          return REFUND_STATUS_OPTIONS as FilterOptionItem[];
        case InvoiceFilterIds.DISPUTE_STATUS:
          return DISPUTE_STATUS_OPTIONS as FilterOptionItem[];
        case InvoiceFilterIds.REMINDER_STATUS:
          return REMINDER_STATUS_OPTIONS as FilterOptionItem[];
        case InvoiceFilterIds.JOB_STATUS:
          return JOB_STATUS_OPTIONS as FilterOptionItem[];
        case InvoiceFilterIds.ESTIMATE_STATUS:
          return ESTIMATE_STATUS_OPTIONS as FilterOptionItem[];
        case InvoiceFilterIds.TIP_COLLECTED:
          return TIP_COLLECTED_OPTIONS as FilterOptionItem[];
        case InvoiceFilterIds.PAYMENT_METHODS:
          return getPaymentMethodFilters().map(item => ({
            id: item.key,
            label: item.value
          })) as FilterOptionItem[];
        default:
          return [];
      }
    },
    [merchantOptions]
  );

  const handleApplyFilterFromMoreFilters = useCallback(
    (filterData: { selectedFilterItems: string[] | AmountFilter; filter: FilterOption }) => {
      const { selectedFilterItems, filter } = filterData;
      const updatedFilters = [...appliedFilters];
      const filterIndex = updatedFilters.findIndex(f => f.id === filter.id);

      let displayValue = '';
      if (filter.type === FilterDropdownType.AMOUNT) {
        const amountFilterValue = selectedFilterItems as AmountFilter;
        setAmountFilter(amountFilterValue);
        if (amountFilterValue.filterOperation === AmountFilterOperation.BW) {
          displayValue = `$${amountFilterValue.minAmount} - $${amountFilterValue.maxAmount}`;
        } else if (amountFilterValue.filterOperation !== AmountFilterOperation.ALL) {
          displayValue = `$${amountFilterValue.minAmount}`;
        }
      } else if (filter.type === FilterDropdownType.DROPDOWN) {
        const selectedItems = selectedFilterItems as string[];
        const filterDataForFilter = getInvoiceFilterData(filter.id);
        if (filter.id === InvoiceFilterIds.PAYMENT_METHODS) {
          displayValue = getTextForFilter(filterDataForFilter, selectedItems, 'id', 'label');
        } else {
          // For single-select filters, show the first selected item
          const selectedOption = filterDataForFilter.find(
            item => item.id === selectedItems[0] || item.value === selectedItems[0]
          );
          displayValue = selectedOption?.label || '';
        }
      }

      if (filterIndex !== -1) {
        updatedFilters[filterIndex] = {
          ...filter,
          value: selectedFilterItems,
          displayValue
        };
      } else {
        updatedFilters.push({
          ...filter,
          value: selectedFilterItems,
          displayValue
        });
      }

      setAppliedFilters(updatedFilters);
    },
    [appliedFilters, getInvoiceFilterData]
  );

  const handleRemoveFilter = useCallback((filterId: string) => {
    switch (filterId) {
      case InvoiceFilterIds.AMOUNT:
        setAmountFilter({
          filterOperation: AmountFilterOperation.ALL,
          minAmount: null,
          maxAmount: null
        });
        break;
      case InvoiceFilterIds.REFUND_STATUS:
      case InvoiceFilterIds.DISPUTE_STATUS:
      case InvoiceFilterIds.REMINDER_STATUS:
      case InvoiceFilterIds.JOB_STATUS:
      case InvoiceFilterIds.ESTIMATE_STATUS:
      case InvoiceFilterIds.TIP_COLLECTED:
      case InvoiceFilterIds.PAYMENT_METHODS:
        // These are handled by the appliedFilters state
        break;
    }
    setAppliedFilters(prev => prev.filter(filter => filter.id !== filterId));
  }, []);

  const handleClearDueDate = () => {
    setDueDateFrom(null);
    setDueDateTo(null);
  };

  const handleClearCreatedDate = () => {
    setCreatedAtFrom(null);
    setCreatedAtTo(null);
  };

  return (
    <div className="flex h-full w-full flex-col gap-4 md:flex-row md:flex-wrap md:items-center md:gap-2">
      <DropdownWithSearch
        searchFilterId="invoice-status-search-field"
        filterId="invoice-status-filter"
        data={INVOICE_STATUS_OPTIONS}
        searchText={statusSearchString}
        setSearchText={setStatusSearchString}
        selectedDraftArray={selectedStatusesDraft}
        setSelectedDraftArray={setSelectedStatusesDraft}
        selectedArray={selectedStatuses}
        setSelectedArray={setSelectedStatuses}
        dataKey="label"
        dataIdKey="id"
        buttonPrimaryText="Status"
        buttonSecondaryText={getTextForFilter(INVOICE_STATUS_OPTIONS, selectedStatuses, 'id', 'label')}
        filterPlaceholderText="Filter by status"
        searchPlaceHolderText="Search status"
        allowSearch={true}
      />

      <DropdownWithSearch
        searchFilterId="invoice-customer-name-search-field"
        filterId="invoice-customer-filter"
        data={customerOptions}
        searchText={customerSearchString}
        setSearchText={setCustomerSearchString}
        selectedDraftArray={selectedCustomersDraft}
        setSelectedDraftArray={setSelectedCustomersDraft}
        selectedArray={selectedCustomers}
        setSelectedArray={setSelectedCustomers}
        dataKey="name"
        secondaryDataKey="lastName"
        dataIdKey="ID"
        buttonPrimaryText="Customers"
        buttonSecondaryText={getTextForFilter(customerOptions, selectedCustomers, 'ID', 'name', 'lastName')}
        filterPlaceholderText="Filter by customers"
        searchPlaceHolderText="Search customer names"
        allowSearch={true}
      />

      <CustomDateRangePicker
        startDate={createdAtFrom}
        setStartDate={setCreatedAtFrom}
        endDate={createdAtTo}
        setEndDate={setCreatedAtTo}
        filterId="invoice-created-date-filter"
        setDateRange={(start: string | null, end: string | null) => {
          setCreatedAtFrom(start);
          setCreatedAtTo(end);
        }}
        placeholder="Date range"
        onClear={handleClearCreatedDate}
      />

      <DropdownWithSearch
        searchFilterId="invoice-merchant-name-search-field"
        filterId="invoice-createdby-filter"
        data={merchantOptions}
        searchText={createdBySearchString}
        setSearchText={setCreatedBySearchString}
        selectedDraftArray={createdByDraft}
        setSelectedDraftArray={setCreatedByDraft}
        selectedArray={createdBy}
        setSelectedArray={setCreatedBy}
        dataKey="firstName"
        secondaryDataKey="lastName"
        dataIdKey="merchantId"
        buttonPrimaryText="Created by"
        buttonSecondaryText={getTextForFilter(merchantOptions, createdBy, 'merchantId', 'firstName', 'lastName')}
        filterPlaceholderText="Filter by merchants"
        searchPlaceHolderText="Search merchant names"
        allowSearch={true}
      />

      <CustomDateRangePicker
        startDate={dueDateFrom}
        setStartDate={setDueDateFrom}
        endDate={dueDateTo}
        setEndDate={setDueDateTo}
        filterId="invoice-due-date-filter"
        setDateRange={(start: string | null, end: string | null) => {
          setDueDateFrom(start);
          setDueDateTo(end);
        }}
        placeholder="Due date"
        onClear={handleClearDueDate}
      />

      <MoreFilters
        onFilterApply={handleApplyFilterFromMoreFilters}
        onFilterRemove={handleRemoveFilter}
        appliedFilters={appliedFilters}
        filterOptions={INVOICE_MORE_FILTER_OPTIONS}
        getFilterData={getInvoiceFilterData}
      />
    </div>
  );
});

InvoicesFilter.displayName = 'InvoicesFilter';

export default InvoicesFilter;
